// src/data/dropdownData.js

// ════════════════════════════════════════════
// Org options per function per request type
// ════════════════════════════════════════════
export const orgOptionsMap = {
    approved: {
        'Operations Team': [
            { label: '-- Select Org --', value: '' },
            { label: 'TSE International', value: 'TSE International' }
        ],
        'Tech Team': [
            { label: '-- Select Org --', value: '' }
        ],
        'Support Function': [
            { label: '-- Select Org --', value: '' },
            { label: 'OEX', value: 'OEX' }
        ]
    },
    unlisted: {
        'Operations Team': [
            { label: '-- Select Org --', value: '' },
            { label: 'OEX', value: 'OEX' },
            { label: 'TSE International', value: 'TSE International' }
        ],
        'Tech Team': [
            { label: '-- Select Org --', value: '' },
            { label: 'OEX', value: 'OEX' },
            { label: 'TSE International', value: 'TSE International' }
        ],
        'Support Function': [
            { label: '-- Select Org --', value: '' },
            { label: 'OEX', value: 'OEX' },
            { label: 'TSE International', value: 'TSE International' }
        ]
    }
};

// ════════════════════════════════════════════
// Teams per function — full list
// ════════════════════════════════════════════
export const teamsData = {
    'Operations Team': [
        { label: 'SPIV', value: 'spiv' },
        { label: 'PoE', value: 'poe' },
        { label: 'PoE International', value: 'poe_international' },
        { label: 'BPBO', value: 'bpbo' },
        { label: 'CDA', value: 'cda' },
        { label: 'RISC', value: 'risc' },
        { label: 'PQ International', value: 'pq_international' },
        { label: 'Others', value: 'others_ops' }
    ],
    'Tech Team': [
        { label: 'SDE', value: 'sde' },
        { label: 'Data Engineering', value: 'de' },
        { label: 'Applied Scientist', value: 'as' },
        { label: 'Others', value: 'others_tech' }
    ],
    'Support Function': [
        { label: 'Training', value: 'training' },
        { label: 'Program Manager', value: 'program_manager' },
        { label: 'Product Management', value: 'product_management' },
        { label: 'Others', value: 'others_support' }
    ]
};

// ════════════════════════════════════════════
// Approved-only teams (restricted paths)
// ════════════════════════════════════════════
const approvedOnlyTeams = {
    'Operations Team': {
        'TSE International': [
            { label: 'PoE International', value: 'poe_international', approvalLink: 'https://approvals.amazon.com/approval/47315200/details/memo-view/notes-and-summary',
              approvedPermissions: ['base_pi', 'create_investigations', 'peeknow_resolved', 'action_widget', 'case_locker', 'uow_history', 'secure_link_nemo', 'unfettered_search']
            }
        ]
    },
    'Support Function': {
        'OEX': [
            { label: 'Training', value: 'training', approvalLink: '', approvedPermissions: [] }
        ]
    }
};

// ════════════════════════════════════════════
// Get filtered teams based on request type, function, and org
// ════════════════════════════════════════════
export const getFilteredTeams = (requestType, functionDropdown, orgName) => {
    if (!functionDropdown || !teamsData[functionDropdown]) return [];

    const allTeams = teamsData[functionDropdown];

    if (requestType === 'approved') {
        // Only show approved teams for the selected function + org
        const approvedForFunction = approvedOnlyTeams[functionDropdown];
        if (!approvedForFunction || !approvedForFunction[orgName]) return [];
        return approvedForFunction[orgName];
    }

    // Unlisted: show all teams EXCEPT the approved-only ones
    const approvedForFunction = approvedOnlyTeams[functionDropdown];
    if (!approvedForFunction || !approvedForFunction[orgName]) return allTeams;

    const approvedValues = approvedForFunction[orgName].map(t => t.value);
    return allTeams.filter(t => !approvedValues.includes(t.value));
};

// ════════════════════════════════════════════
// Get org options based on request type and function
// ════════════════════════════════════════════
export const getOrgOptions = (requestType, functionDropdown) => {
    if (!functionDropdown) return [{ label: '-- Select Org --', value: '' }];
    const type = requestType || 'approved';
    const options = orgOptionsMap[type]?.[functionDropdown];
    return options || [{ label: '-- Select Org --', value: '' }];
};

// ════════════════════════════════════════════
// Get approval link for a pre-approved team
// ════════════════════════════════════════════
export const getApprovalLink = (functionDropdown, orgName, teamValue) => {
    const approvedForFunction = approvedOnlyTeams[functionDropdown];
    if (!approvedForFunction || !approvedForFunction[orgName]) return '';
    const team = approvedForFunction[orgName].find(t => t.value === teamValue);
    return team?.approvalLink || '';
};

// ════════════════════════════════════════════
// Get approved permission IDs for a pre-approved team
// ════════════════════════════════════════════
export const getApprovedPermissionIds = (functionDropdown, orgName, teamValue) => {
    const approvedForFunction = approvedOnlyTeams[functionDropdown];
    if (!approvedForFunction || !approvedForFunction[orgName]) return [];
    const team = approvedForFunction[orgName].find(t => t.value === teamValue);
    return team?.approvedPermissions || [];
};

export const useCasesData = {
    'SPIV': [
        { label: 'Option 1', value: 'option_1' },
        { label: 'Option 2', value: 'option_2' },
        { label: 'Option 3', value: 'option_3' }
    ],
    'PoE': [
        { label: 'Document Verification', value: 'doc_verification' },
        { label: 'Identity Validation', value: 'identity_validation' }
    ],
    'PoE International': [
        { label: 'To take reinstate/enforcement action', value: 'reinstate_enforcement_action' }
    ],
    'BPBO': [
        { label: 'Process Optimization', value: 'process_opt' },
        { label: 'Workflow Automation', value: 'workflow_auto' }
    ],
    'CDA': [
        { label: 'Data Analysis', value: 'data_analysis' },
        { label: 'Reporting', value: 'reporting' }
    ],
    'RISC': [
        { label: 'Risk Modeling', value: 'risk_modeling' },
        { label: 'Threat Detection', value: 'threat_detection' }
    ],
    'PQ International': [
        { label: 'As a PQ international RM and Investigator, deep dive seller, brand, and listing data across international stores to reduce counterfeit products, improve product quality, and minimize seller friction', value: 'pq_international_usecase' }
    ],
    'SDE': [
        { label: 'API Development', value: 'api_dev' },
        { label: 'System Integration', value: 'sys_integration' }
    ],
    'Data Engineering': [
        { label: 'ETL Pipeline', value: 'etl_pipeline' },
        { label: 'Data Warehouse', value: 'data_warehouse' }
    ],
    'Applied Scientist': [
        { label: 'ML Model Development', value: 'ml_model' },
        { label: 'Predictive Analytics', value: 'predictive' }
    ],
    'Training': [
        { label: 'Training demos: UFS is part of the Ops SOP and trainers need to demonstrate it during training sessions', value: 'training_demos' },
        { label: 'Ramp support: Trainers validate associates final actions before case submission during post-training ramp periods', value: 'ramp_support' },
        { label: 'Live examples: Real-time demonstrations are required for effective training delivery', value: 'live_examples' }
    ],
    'Program Manager': [
        { label: 'Project Planning', value: 'project_planning' },
        { label: 'Resource Management', value: 'resource_mgmt' }
    ],
    'Product Management': [
        { label: 'Feature Development', value: 'feature_dev' },
        { label: 'User Research', value: 'user_research' }
    ],
    'Others': [
        { label: 'General Task', value: 'general_task' },
        { label: 'Other Request', value: 'other_request' }
    ]
};

export const functionOptions = [
    { label: '-- Select Function --', value: '' },
    { label: 'Operations Team', value: 'Operations Team' },
    { label: 'Tech Team', value: 'Tech Team' },
    { label: 'Support Function', value: 'Support Function' }
];

export const functionDescriptions = {
    'Operations Team': 'Select this option if you belong to the Operations job family and require UFS access as part of your process SOP.',
    'Tech Team': 'Select this option if you belong to job families such as SDE, Applied Scientist, or Data Engineering, and require UFS access for widget testing, Paragon testing, troubleshooting, or related activities.',
    'Support Function': 'Select this option if you belong to job families such as Program Management, Training, or Product Management, and require UFS access for deep dives, special projects, or other related activities.'
};

// Legacy export — kept for backward compatibility
export const orgOptions = [
    { label: '-- Select Org --', value: '' },
    { label: 'OEX', value: 'OEX' },
    { label: 'TSE International', value: 'TSE International' }
];

export const userLevelOptions = ['L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8', 'L10'];

export const frequencyOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
];

export const searchCriteriaOptions = [
    { label: 'Seller ID', value: 'seller_id' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'SAFE-T Claim ID', value: 'safet_claim_id' },
    { label: 'Order ID', value: 'order_id' },
    { label: 'Investigation ID', value: 'investigation_id' },
    { label: 'Credit Card ID', value: 'credit_card_id' },
    { label: 'Customer Name', value: 'customer_name' },
    { label: 'Contact ID', value: 'contact_id' },
    { label: 'A-to-Z Claim ID', value: 'atoz_claim_id' }
];

export const priorityOptions = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' }
];

// ════════════════════════════════════════════
// T321 "User Details" — Org → Function → Process cascade
// (unlisted path only). Labels store the full string (acronym + name)
// so approvals.amazon.com memos and the approver app are self-documenting.
// ════════════════════════════════════════════

// Top-level Org Name dropdown
export const t321OrgNameOptions = [
    { label: '-- Select Org Name --', value: '' },
    { label: 'SPIV', value: 'SPIV' },
    { label: 'Non-SPIV', value: 'Non-SPIV' },
    { label: 'Others', value: 'Others' }
];

// SPIV functions (dropdown). Non-SPIV / Others use a free-text Function field.
export const t321SpivFunctionOptions = [
    { label: '-- Select Function --', value: '' },
    { label: 'VRMO (VERIFICATION AND RISK MANAGEMENT OPERATIONS)', value: 'VRMO (VERIFICATION AND RISK MANAGEMENT OPERATIONS)' },
    { label: 'IT (INVENTORY TRUST)', value: 'IT (INVENTORY TRUST)' },
    { label: 'SLM (SELLER LIFECYCLE MANAGEMENT)', value: 'SLM (SELLER LIFECYCLE MANAGEMENT)' }
];

// Process dropdown, keyed by the selected SPIV function value.
// SLM has no process → omitted from the map (getT321ProcessOptions returns []).
export const t321ProcessOptionsMap = {
    'VRMO (VERIFICATION AND RISK MANAGEMENT OPERATIONS)': [
        { label: '-- Select Process --', value: '' },
        { label: 'IPI (In Person Investigations)', value: 'IPI (In Person Investigations)' },
        { label: 'IPV (In person Verifications)', value: 'IPV (In person Verifications)' },
        { label: 'FP (Fraud preventions)', value: 'FP (Fraud preventions)' },
        { label: 'KYC/VAT (Know your customer)', value: 'KYC/VAT (Know your customer)' },
        { label: 'SVR (Seller Verification at registration)', value: 'SVR (Seller Verification at registration)' }
    ],
    'IT (INVENTORY TRUST)': [
        { label: '-- Select Process --', value: '' },
        { label: 'IE (Inventory Evaluation)', value: 'IE (Inventory Evaluation)' },
        { label: 'INSPIRE (Investigations of Seller Practices, Inventory and Risk Evaluation)', value: 'INSPIRE (Investigations of Seller Practices, Inventory and Risk Evaluation)' },
        { label: 'FBA Risk (Fulfilled by Amazon)', value: 'FBA Risk (Fulfilled by Amazon)' }
    ]
    // 'SLM (SELLER LIFECYCLE MANAGEMENT)' → no process
};

// Legal Entity — multi-select checkboxes (new addition). Shown for all orgs.
export const legalEntityOptions = [
    'APE',
    'APUK',
    'ACSK',
    'KYC Asterix',
    'KYC Seller Wallet API',
    'KYC AWS',
    'KYC BR',
    'KYC CA'
];

// ── Helpers ──────────────────────────────────────────────────────────────

// True when Org=SPIV → Function is a dropdown; otherwise Function is free text.
export const isT321SpivOrg = (orgName) => orgName === 'SPIV';

// Function options for the given org. Non-SPIV / Others return [] (free-text field).
export const getT321FunctionOptions = (orgName) =>
    isT321SpivOrg(orgName) ? t321SpivFunctionOptions : [];

// Process options for the given SPIV function value. Returns [] when the function
// has no process (SLM) or when org is not SPIV.
export const getT321ProcessOptions = (orgName, functionValue) => {
    if (!isT321SpivOrg(orgName)) return [];
    return t321ProcessOptionsMap[functionValue] || [];
};
